/*
 * Tame the Wook — fx.js
 * Shared motion & atmosphere kit for the storefront design.
 *
 *   <ttw-smoke>            canvas "scent trail" ribbons (brand-palette curl lines,
 *                          additive glow, pointer drift, trails). Absolute-position it
 *                          inside a relatively-positioned section. Attributes:
 *                            density="5"   ribbon count
 *                            opacity="0.6" overall strength
 *   [data-tilt]            3D perspective tilt toward the pointer (desktop only).
 *                          Optional value = max degrees, default 6.
 *   [data-reveal]          fade+rise on first scroll into view (skipped for
 *                          reduced-motion / no IntersectionObserver).
 *                          Optional value = extra delay in ms.
 *
 * Everything degrades to static, fully-visible content when JS is off,
 * motion is reduced, or the device has no hover.
 */
(function () {
  if (window.__ttwFxLoaded) return;
  window.__ttwFxLoaded = true;

  var reduced = false;
  try { reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
  var noHover = false;
  try { noHover = window.matchMedia('(hover: none)').matches; } catch (e) {}

  // WCAG 2.2.2: an on-page toggle (footer) can pause all decorative motion by
  // setting <html data-motion="off">. Smoke honors it live via this helper.
  function motionOff() { return document.documentElement.getAttribute('data-motion') === 'off'; }

  /* ─────────────────────────── ttw-smoke ─────────────────────────── */
  var PALETTE = [
    ['#52c9c4', '#7c5cdb'],
    ['#cf58ab', '#eec089'],
    ['#eec089', '#52c9c4'],
    ['#7c5cdb', '#cf58ab'],
    ['#52c9c4', '#eec089'],
    ['#cf58ab', '#7c5cdb']
  ];

  function TTWSmokeFactory() {
    function rand(a, b) { return a + Math.random() * (b - a); }

    class TTWSmoke extends HTMLElement {
      constructor() {
        super();
        var root = this.attachShadow({ mode: 'open' });
        var style = document.createElement('style');
        style.textContent = ':host{display:block;overflow:hidden}canvas{width:100%;height:100%;display:block}';
        this.canvas = document.createElement('canvas');
        root.appendChild(style);
        root.appendChild(this.canvas);
        this.ctx = this.canvas.getContext('2d');
        this.ribbons = [];
        this.t = Math.random() * 1000;
        this.running = false;
        this.visible = false;
        this.pointer = { x: 0.5, y: 0.5, tx: 0.5, ty: 0.5 };
        this._raf = null;
      }

      connectedCallback() {
        var self = this;
        var density = parseInt(this.getAttribute('density') || '5', 10);
        this.alpha = parseFloat(this.getAttribute('opacity') || '0.6');
        this.ribbons = [];
        for (var i = 0; i < density; i++) {
          this.ribbons.push({
            y: rand(0.18, 0.85),
            amp1: rand(0.05, 0.14), f1: rand(0.9, 1.8), s1: rand(0.12, 0.3),
            amp2: rand(0.02, 0.07), f2: rand(2.2, 4.2), s2: rand(0.2, 0.5),
            w: rand(1.6, 3.4),
            drift: rand(0.35, 1),
            colors: PALETTE[i % PALETTE.length]
          });
        }
        this._resize = function () { self.resize(); };
        this.ro = new ResizeObserver(this._resize);
        this.ro.observe(this);
        this.io = new IntersectionObserver(function (entries) {
          self.visible = entries[0].isIntersecting;
          self.toggle();
        }, { threshold: 0.02 });
        this.io.observe(this);
        this._vis = function () { self.toggle(); };
        document.addEventListener('visibilitychange', this._vis);
        this._move = function (e) {
          var r = self.getBoundingClientRect();
          if (!r.width) return;
          self.pointer.tx = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
          self.pointer.ty = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
        };
        if (!noHover) window.addEventListener('pointermove', this._move, { passive: true });
        this.resize();
        if (reduced || motionOff()) { this.drawFrame(); return; } // one static frame
        this.toggle();
      }

      disconnectedCallback() {
        this.running = false;
        if (this._raf) cancelAnimationFrame(this._raf);
        if (this.ro) this.ro.disconnect();
        if (this.io) this.io.disconnect();
        document.removeEventListener('visibilitychange', this._vis);
        window.removeEventListener('pointermove', this._move);
      }

      resize() {
        var dpr = Math.min(window.devicePixelRatio || 1, 1.5);
        var w = this.clientWidth || 300, h = this.clientHeight || 200;
        this.canvas.width = Math.round(w * dpr);
        this.canvas.height = Math.round(h * dpr);
        this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.W = w; this.H = h;
        this.ctx.clearRect(0, 0, w, h);
      }

      toggle() {
        var should = this.visible && !document.hidden && !reduced && !motionOff();
        if (should && !this.running) { this.running = true; this._lastNow = null; this.loop(); }
        if (!should) { this.running = false; if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; } }
      }

      loop(now) {
        if (!this.running) return;
        var self = this;
        // Advance by real elapsed time (clamped) so the drift speed is identical on 60Hz
        // and 144Hz displays; the old fixed +0.016/frame ran 2.4x fast at 144Hz. The clamp
        // also swallows the jump after a tab was hidden or the loop was paused.
        var dt = 0.016;
        if (typeof now === 'number') {
          if (this._lastNow != null) dt = Math.min(Math.max((now - this._lastNow) / 1000, 0), 0.05);
          this._lastNow = now;
        }
        this.t += dt;
        this.pointer.x += (this.pointer.tx - this.pointer.x) * 0.04;
        this.pointer.y += (this.pointer.ty - this.pointer.y) * 0.04;
        this.drawFrame();
        this._raf = requestAnimationFrame(function (ts) { self.loop(ts); });
      }

      drawFrame() {
        var ctx = this.ctx, W = this.W, H = this.H, t = this.t;
        if (!W || !H) return;
        // fade previous frame → trails
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = 'rgba(0,0,0,0.085)';
        ctx.fillRect(0, 0, W, H);
        ctx.globalCompositeOperation = 'lighter';
        var px = (this.pointer.x - 0.5), py = (this.pointer.y - 0.5);
        for (var i = 0; i < this.ribbons.length; i++) {
          var r = this.ribbons[i];
          var grad = ctx.createLinearGradient(0, 0, W, 0);
          grad.addColorStop(0, r.colors[0]);
          grad.addColorStop(1, r.colors[1]);
          var baseY = (r.y + py * 0.06 * r.drift) * H;
          var ampBoost = 1 + Math.abs(px) * 0.5;
          // soft halo pass then bright core pass
          for (var pass = 0; pass < 2; pass++) {
            ctx.beginPath();
            for (var x = -30; x <= W + 30; x += 18) {
              var u = x / W;
              var y = baseY +
                Math.sin(u * Math.PI * 2 * r.f1 + t * r.s1 * 4 + i * 1.7) * r.amp1 * H * ampBoost +
                Math.sin(u * Math.PI * 2 * r.f2 - t * r.s2 * 4 + i * 0.9) * r.amp2 * H;
              if (x === -30) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.strokeStyle = grad;
            ctx.lineCap = 'round';
            if (pass === 0) { ctx.globalAlpha = 0.05 * this.alpha; ctx.lineWidth = r.w * 7; }
            else { ctx.globalAlpha = 0.32 * this.alpha; ctx.lineWidth = r.w; }
            ctx.stroke();
          }
        }
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = 'source-over';
      }
    }
    return TTWSmoke;
  }

  if (window.customElements && !customElements.get('ttw-smoke') && window.ResizeObserver && window.IntersectionObserver) {
    customElements.define('ttw-smoke', TTWSmokeFactory());
  }

  // Called by the footer motion toggle after it flips <html data-motion>. Each
  // smoke element's own toggle() re-reads motionOff(), so this starts/stops them live.
  window.ttwApplyMotion = function () {
    document.querySelectorAll('ttw-smoke').forEach(function (el) { if (el.toggle) el.toggle(); });
  };

  /* ─────────────────────────── [data-tilt] ─────────────────────────── */
  if (!reduced && !noHover) {
    var tiltEl = null;
    function resetTilt() {
      if (!tiltEl) return;
      if (tiltEl.isConnected) {
        tiltEl.style.transition = 'transform .6s cubic-bezier(.2,.7,.2,1)';
        tiltEl.style.transform = 'perspective(950px) rotateX(0deg) rotateY(0deg)';
      }
      tiltEl = null;
    }
    document.addEventListener('pointerover', function (e) {
      var el = e.target && e.target.closest && e.target.closest('[data-tilt]');
      if (el && el !== tiltEl) tiltEl = el;
    }, { passive: true });
    // The pointermove reset below never fires if the cursor exits the browser window
    // entirely — the tilt would stay frozen mid-rotation. Catch the window exit too.
    document.documentElement.addEventListener('mouseleave', resetTilt, { passive: true });
    document.addEventListener('pointercancel', resetTilt, { passive: true });
    document.addEventListener('pointermove', function (e) {
      if (!tiltEl) return;
      if (!tiltEl.isConnected) { tiltEl = null; return; }
      var r = tiltEl.getBoundingClientRect();
      if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) {
        resetTilt();
        return;
      }
      var max = parseFloat(tiltEl.getAttribute('data-tilt')) || 6;
      var rx = ((e.clientY - r.top) / r.height - 0.5) * -2 * max;
      var ry = ((e.clientX - r.left) / r.width - 0.5) * 2 * max;
      tiltEl.style.transition = 'transform .09s linear';
      tiltEl.style.transform = 'perspective(950px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
      tiltEl.style.willChange = 'transform';
    }, { passive: true });
  }

  /* ─────────────────────────── [data-reveal] ─────────────────────────── */
  if (!reduced && window.IntersectionObserver) {
    var seen = new WeakSet();
    var rio = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        rio.unobserve(el);
        var delay = parseInt(el.getAttribute('data-reveal') || '0', 10) || 0;
        setTimeout(function () {
          el.style.transition = 'opacity .75s ease, transform .75s cubic-bezier(.2,.7,.2,1)';
          el.style.opacity = '1';
          el.style.transform = 'none';
          setTimeout(function () {
            // hand style ownership back so future re-renders stay canonical
            el.style.removeProperty('opacity');
            // If the tilt handler owns the transform right now (its perspective() signature),
            // stripping it would visibly snap the element flat mid-hover — leave tilt's
            // inline styles alone; it resets itself on pointer exit.
            if ((el.style.transform || '').indexOf('perspective(') === -1) {
              el.style.removeProperty('transform');
              el.style.removeProperty('transition');
            }
          }, 900);
        }, delay);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -4% 0px' });

    var initReveal = function (el) {
      if (seen.has(el)) return;
      seen.add(el);
      el.style.opacity = '0';
      el.style.transform = 'translateY(26px)';
      rio.observe(el);
    };
    var scan = function (root) {
      if (root.querySelectorAll) root.querySelectorAll('[data-reveal]').forEach(initReveal);
    };
    var boot = function () {
      scan(document);
      new MutationObserver(function (muts) {
        muts.forEach(function (m) {
          m.addedNodes.forEach(function (n) {
            if (n.nodeType !== 1) return;
            if (n.hasAttribute && n.hasAttribute('data-reveal')) initReveal(n);
            scan(n);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) boot(); else document.addEventListener('DOMContentLoaded', boot);
  }
})();
